import { MutationTree } from 'vuex'
import { PageResult, ReferralView, ReferralFilterDirection, ReferralAge, OrderStatus } from '@/api/types'
import { Order } from '@/api/model/order'
import { OrdersState, OrdersStateFilters } from '@/store/orders'
import { Branch } from '@/api/model/branch'
import { User } from '@/api/model/user'
import { OrderCount } from '@/api/model/order-count'

export const mutations: MutationTree<OrdersState> = {
    fetchData(state: OrdersState): void {
        state.loading = true
        state.totalItems = null
        state.error = null
    },

    fetchDataSuccess(state: OrdersState, result: PageResult<Order>): void {
        state.loading = false
        state.error = null
        state.orders = result.collection
        state.totalItems = result.totalItems
        state.totalPages = result.totalPages
    },

    fetchDataFailure(state: OrdersState, error: string): void {
        state.loading = false
        state.error = error
    },

    setPage(state: OrdersState, payload: { page: number }): void {
        state.page = payload.page
    },

    setItemsPerPage(state: OrdersState, payload: { itemsPerPage: number }): void {
        state.itemsPerPage = payload.itemsPerPage
    },

    applyView(state: OrdersState, payload: { view: ReferralView, direction: ReferralFilterDirection }): void {
        state.view = payload.view
        state.direction = payload.direction
    },

    applySearch(state: OrdersState, payload: { query: string }): void {
        state.search = payload.query
    },

    applyOrder(state: OrdersState, payload: { field: string, direction: string }): void {
        state.orderField = payload.field
        state.orderDirection = payload.direction
    },

    applyTypeFilter(state: OrdersState, payload: { typeFilter: string[] }): void {
        state.typeFilter = payload.typeFilter
    },

    applyStatusFilter(state: OrdersState, payload: { statusFilter: string }): void {
        state.statusFilter = payload.statusFilter
    },

    applyReferredByFilter(state: OrdersState, payload: { referredByFilter: User[] }): void {
        state.referredByFilter = payload.referredByFilter
    },

    applyReferredByBranchFilter(state: OrdersState, payload: { referredByBranchFilter: Branch[] }): void {
        state.referredByBranchFilter = payload.referredByBranchFilter
    },

    applyAssignedToFilter(state: OrdersState, payload: { assignedToFilter: User[] }): void {
        state.assignedToFilter = payload.assignedToFilter
    },

    applyAssignedToBranchFilter(state: OrdersState, payload: { assignedToBranchFilter: Branch[] }): void {
        state.assignedToBranchFilter = payload.assignedToBranchFilter
    },

    applyDateSpecificFilter(state: OrdersState, payload: { day: Date }): void {
        state.dateSpecificFilter = payload.day
    },

    applyDateRangeFilter(state: OrdersState, payload: { range: string }): void {
        state.dateRangeFilter = payload.range
    },

    applyAgeFilter(state: OrdersState, payload: { ageFilter: ReferralAge }): void {
        state.ageFilter = payload.ageFilter
    },

    applyFilters(state: OrdersState, payload: OrdersStateFilters): void {
        Object.keys(payload).forEach(key => {
            state[key] = payload[key]
        })
    },

    /**
     * Resets all filters to the store default.
     * Has no action and is ment to be used in combination with 'applyFilters'
     */
    clearFilters(state: OrdersState): void {
        state.view = 'user'
        state.direction = 'in'
        state.search = null
        state.typeFilter = []
        state.statusFilter = OrderStatus.PENDING
        state.referredByFilter = []
        state.referredByBranchFilter = []
        state.assignedToFilter = []
        state.assignedToBranchFilter = []
        state.dateSpecificFilter = null
        state.dateRangeFilter = null
        state.ageFilter = null
    },

    /**
     * Restore to the previously stored snapshot of filters.
     */
    resetFilters(state: OrdersState): void {
        state.search = null
        state.typeFilter = state.snapshot.typeFilter
        state.referredByFilter = state.snapshot.referredByFilter
        state.referredByBranchFilter = state.snapshot.referredByBranchFilter
        state.assignedToFilter = state.snapshot.assignedToFilter
        state.assignedToBranchFilter = state.snapshot.assignedToBranchFilter
        state.dateSpecificFilter = state.snapshot.dateSpecificFilter
        state.dateRangeFilter = state.snapshot.dateRangeFilter
        state.ageFilter = state.snapshot.ageFilter
    },

    /**
     * Take a snapshot of the current filters.
     * These can be restored by comitting 'resetFilters'
     */
    snapshot(state: OrdersState): void {
        state.snapshot.typeFilter = state.typeFilter
        state.snapshot.referredByFilter = state.referredByFilter
        state.snapshot.referredByBranchFilter = state.referredByBranchFilter
        state.snapshot.assignedToFilter = state.assignedToFilter
        state.snapshot.assignedToBranchFilter = state.assignedToBranchFilter
        state.snapshot.dateSpecificFilter = state.dateSpecificFilter
        state.snapshot.dateRangeFilter = state.dateRangeFilter
        state.snapshot.ageFilter = state.ageFilter
    },

    updateMenuCount(state: OrdersState): void {
        state.orderCounts = {
            pendingOrders: 0,
            successfulOrders: 0,
            unsuccessfulOrders: 0,
        } as OrderCount
    },

    updateMenuCountSuccess(state: OrdersState, payload: OrderCount): void {
        state.orderCounts = payload
    },

    updateMenuCountFailure(state: OrdersState, error: string): void {
        state.error = error
    },
}
